/* storybook-check-ignore */
import { Button, Icon } from '@opendoor/bricks/core';
import SelectiveSpritesheet from '@opendoor/bricks/core/Icon/SelectiveSpritesheet';
import { useEffect, useReducer, useCallback } from 'react';
import { getAthenaClient } from '../../api';
import Favorite from '@opendoor/bricks/core/Icon/SpritesheetIcons/Favorite';
import FavoriteFilled from '@opendoor/bricks/core/Icon/SpritesheetIcons/FavoriteFilled';
import { useAuth } from '@opendoor/auth-fe';
import { useRouter } from 'next/router';
import useTrackProperty from '../helpers/useTrackProperty';

type FavoriteState = {
  isFavorite: boolean;
  isFetching: boolean;
};
const initialState: FavoriteState = {
  isFavorite: false,
  isFetching: false,
};

type FavoriteActions =
  | {
      type: 'START_TOGGLE_FAVORITE';
    }
  | {
      type: 'SET_FAVORITE';
      payload: { isFavorite: boolean };
    };

const reducer: (state: FavoriteState, action: FavoriteActions) => FavoriteState = (
  state,
  action,
) => {
  switch (action.type) {
    case 'START_TOGGLE_FAVORITE':
      if (state.isFavorite) {
        return { isFavorite: false, isFetching: true };
      }
      return { isFavorite: true, isFetching: true };
    case 'SET_FAVORITE':
      return { isFavorite: action.payload.isFavorite, isFetching: false };
  }
};

const checkIfFavorited = async (addressId: string) => {
  try {
    const olaId = (await getAthenaClient().GetOlaIdForAddress({ addressId })).buyer
      ?.getPropertyByAddressId?.property?.id;
    if (!olaId) {
      return false;
    }
    const res = await getAthenaClient().GetIsFavoriteByOlaPropertyId({ id: olaId });
    return res.buyer.getIsFavoritePropertyByIds?.favoritePropertyData[0].isFavorite ?? false;
  } catch {
    return false;
  }
};

const favoriteProperty = async (addressId: string) => {
  try {
    const res = await getAthenaClient().FavoriteProperty({ input: { addressId } });
    return res.buyer.favoriteProperty?.property?.isFavorite ?? false;
  } catch {
    return false;
  }
};

const removeFavoriteProperty = async (addressId: string) => {
  try {
    const res = await getAthenaClient().RemoveFavoriteProperty({ input: { addressId } });
    return res.buyer.removeFavoriteProperty?.property?.isFavorite ?? true;
  } catch {
    return true;
  }
};

function redirectToLogin(addressId: string) {
  const url = new URL(window.location.toString());
  url.searchParams.append('favoriteId', addressId);
  window.location.assign(`/login?redirectTo=${encodeURIComponent(url.toString())}`);
  return;
}

const FavoriteButton: React.FC<{ addressId: string }> = ({ addressId }) => {
  const router = useRouter();
  const { authentication } = useAuth();
  const isLoggedIn = authentication.state === 'authenticated';
  const favoriteIdExists = router.query['favoriteId'] === addressId;
  const onResetFavoriteId = () => {
    delete router.query['favoriteId'];
    router.replace(
      {
        query: router.query,
      },
      undefined,
      { shallow: true },
    );
  };
  return (
    <FavoriteButtonInternal
      addressId={addressId}
      isLoggedIn={isLoggedIn}
      favoriteIdExists={favoriteIdExists}
      onFinishFavoriteId={onResetFavoriteId}
    />
  );
};

// Props passed in from FavoriteButton to make testing easier.
// No need to mock nextjs router, auth context, etc.
export const FavoriteButtonInternal: React.FC<{
  addressId: string;
  isLoggedIn: boolean;
  favoriteIdExists: boolean;
  onFinishFavoriteId: () => void;
}> = ({ addressId, isLoggedIn, favoriteIdExists, onFinishFavoriteId }) => {
  const { trackProperty } = useTrackProperty();
  const [state, dispatch] = useReducer(reducer, initialState);
  // if logged in effect
  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    if (favoriteIdExists) {
      dispatch({ type: 'START_TOGGLE_FAVORITE' });
      favoriteProperty(addressId)
        .then((res) => {
          dispatch({ type: 'SET_FAVORITE', payload: { isFavorite: res } });
        })
        .finally(() => {
          onFinishFavoriteId();
        });
      return;
    }
    checkIfFavorited(addressId).then((res) => {
      dispatch({ type: 'SET_FAVORITE', payload: { isFavorite: res } });
    });
  }, [isLoggedIn, favoriteIdExists]);
  const onClickFavorite = useCallback(async () => {
    if (!isLoggedIn) {
      trackProperty('property-favorite', 'redirect-to-login');
      return redirectToLogin(addressId);
    }
    trackProperty('property-favorite', 'logged-in');
    dispatch({ type: 'START_TOGGLE_FAVORITE' });
    const favoriteTrackingLabel = state.isFavorite ? 'remove-favorite' : 'add-favorite';
    const favoriteAction = state.isFavorite ? removeFavoriteProperty : favoriteProperty;
    dispatch({ type: 'SET_FAVORITE', payload: { isFavorite: await favoriteAction(addressId) } });
    trackProperty('property-favorite', favoriteTrackingLabel);
  }, [state.isFavorite, isLoggedIn]);
  return (
    <>
      <SelectiveSpritesheet icons={[Favorite, FavoriteFilled]} />
      <Button
        variant="icon"
        aria-label="Favorite this property"
        analyticsName="cosmos-pdp-favorite-button"
        disabled={state.isFetching}
        type="button"
        onClick={() => {
          onClickFavorite();
        }}
      >
        <Icon name={state.isFavorite ? 'favorite-filled' : 'favorite'} size={16}></Icon>
      </Button>
    </>
  );
};

export default FavoriteButton;
